import { useState, useEffect, useRef } from "react";
import { X, User, BookOpen, Trash2, ArrowRight, AlertTriangle, ScanLine } from "lucide-react";
import toast from "react-hot-toast";
import api from "../../connector/api";

export default function CheckoutModal({ open, onClose, onSuccess }) {
  const [studentBarcode, setStudentBarcode] = useState("");
  const [bookBarcode, setBookBarcode] = useState("");
  const [student, setStudent] = useState(null);
  const [booksToCheckout, setBooksToCheckout] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const studentInputRef = useRef();
  const bookInputRef = useRef();

  useEffect(() => {
    if (open) {
      setStudentBarcode("");
      setBookBarcode("");
      setStudent(null);
      setBooksToCheckout([]);
      setTimeout(() => studentInputRef.current?.focus(), 150);
    }
  }, [open]);

  const handleStudentScan = async (e) => {
    if (e.key !== "Enter") return;
    if (!studentBarcode.trim()) return;
    await fetchStudent(studentBarcode);
  };

  const fetchStudent = async (barcode) => {
    try {
      const res = await api.get(`/patrons/barcode/${barcode}/`);
      setStudent(res.data);
      setBooksToCheckout([]);
      setBookBarcode("");
      setTimeout(() => bookInputRef.current?.focus(), 150);
    } catch {
      toast.error("Patron record not found");
      setStudent(null);
    }
  };

  const handleBookScan = async (e) => {
    if (e.key !== "Enter") return;
    if (!bookBarcode.trim()) return;
    if (!student) return toast.error("Please scan or verify patron first");

    const activeLoansCount = (student.active_loans || []).length;
    if (activeLoansCount + booksToCheckout.length >= 5) {
      return toast.error("Patron has reached the maximum upper limit of 5 total items");
    }

    try {
      const res = await api.get(`/cataloging/barcode/${bookBarcode}/`);
      const book = res.data;

      if (booksToCheckout.some((b) => b.id === book.id)) {
        setBookBarcode("");
        return toast.error("This item has already been staged in the checkout list");
      }

      setBooksToCheckout((prev) => [...prev, book]);
      setBookBarcode("");
    } catch {
      toast.error("Book inventory barcode records not found");
    }
  };

  const handleRemoveBook = (bookId) => {
    setBooksToCheckout((prev) => prev.filter((b) => b.id !== bookId));
  };

  const handleCheckout = async () => {
    if (!student) return toast.error("Scan a patron to begin");
    if (booksToCheckout.length === 0) return toast.error("Staging shelf is empty");

    setIsSubmitting(true);
    const remainingBooks = [];

    for (let book of booksToCheckout) {
      try {
        if (!book.is_available) {
          toast.error(`"${book.title}" is currently marked active on another loan ledger`);
          remainingBooks.push(book);
          continue;
        }

        await api.post("/loans/checkout/", {
          patron: student.id,
          book: book.id,
        });
      } catch (err) {
        toast.error(err.response?.data?.error || `Failed to process loan for "${book.title}"`);
        remainingBooks.push(book);
      }
    }

    toast.success("Fulfillment ledgers processed successfully");
    setBooksToCheckout(remainingBooks);

    if (studentBarcode) {
      await fetchStudent(studentBarcode);
    } else {
      setStudent(null);
    }

    onSuccess();
    setIsSubmitting(false);
  };

  const getLoanStatusStyles = (dueDate) => {
    const today = new Date().toISOString().split("T")[0];
    if (dueDate < today) return { bg: "bg-rose-500/10 text-rose-400 border-rose-500/20", dot: "bg-rose-500", label: "Overdue" };
    if (dueDate === today) return { bg: "bg-amber-500/10 text-amber-400 border-amber-500/20", dot: "bg-amber-500", label: "Due Today" };
    return { bg: "bg-[#1a1613] text-stone-300 border-[#2e2720]", dot: "bg-emerald-500", label: "Active" };
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-stone-950/80 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fadeIn">
      <div className="bg-[#1a1613] rounded-2xl shadow-2xl max-w-4xl w-full border border-[#2e2720] overflow-hidden flex flex-col max-h-[90vh] transform transition-all font-dynamic-title">
        
        {/* Modal Header Panel */}
        <div className="px-6 py-4 border-b border-[#2e2720] flex items-center justify-between bg-[#131110]">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-amber-500/10 border border-amber-500/20 rounded-xl text-amber-400">
              <ScanLine className="h-5 w-5" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-stone-100">Desk Checkout Terminal</h3>
              <p className="text-xs text-stone-400 mt-0.5">Scan institutional borrower profiles and item catalog tags to open new loans.</p>
            </div>
          </div>
          <button 
            onClick={onClose} 
            className="text-stone-400 hover:text-stone-200 hover:bg-[#201c17] p-1.5 rounded-xl transition"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Modal Dynamic Workspace Split Layout Content Body */}
        <div className="flex-1 overflow-y-auto p-6 grid grid-cols-1 lg:grid-cols-12 gap-6 min-h-0">
          
          {/* Left Column: Input Scanners and Patron Dossiers (Grid Size 5) */}
          <div className="lg:col-span-5 space-y-5 flex flex-col justify-between lg:border-r lg:border-[#2e2720] lg:pr-6">
            <div className="space-y-4">
              {/* Step 1: Patron Identity Verification Box */}
              <div className="space-y-1.5">
                <label className="text-[10px] font-bold text-stone-400 uppercase tracking-wide flex items-center gap-1.5">
                  <span>1. Patron Scanner</span>
                  {!student && <span className="inline-block h-1.5 w-1.5 bg-amber-400 rounded-full animate-pulse" />}
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-2.5 h-4 w-4 text-stone-500" />
                  <input
                    ref={studentInputRef}
                    type="text"
                    placeholder="Scan card barcode + press Enter..."
                    value={studentBarcode}
                    onChange={(e) => setStudentBarcode(e.target.value)}
                    onKeyDown={handleStudentScan}
                    className={`w-full bg-[#131110] border text-stone-200 pl-9 pr-3 py-2 rounded-xl text-xs placeholder-stone-500 focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500/50 transition font-mono ${!student ? 'border-amber-500/40 bg-amber-500/5' : 'border-[#2e2720]'}`}
                  />
                </div>
              </div>

              {/* Step 2: Resource Ledger Scanner Box */}
              <div className="space-y-1.5">
                <label className="text-[10px] font-bold text-stone-400 uppercase tracking-wide flex items-center gap-1.5">
                  <span>2. Catalog Copy Scanner</span>
                  {student && booksToCheckout.length === 0 && <span className="inline-block h-1.5 w-1.5 bg-amber-400 rounded-full animate-pulse" />}
                </label>
                <div className="relative">
                  <BookOpen className="absolute left-3 top-2.5 h-4 w-4 text-stone-500" />
                  <input
                    ref={bookInputRef}
                    type="text"
                    placeholder={student ? "Scan book tag barcode + press Enter..." : "Awaiting patron authorization..."}
                    value={bookBarcode}
                    onChange={(e) => setBookBarcode(e.target.value)}
                    onKeyDown={handleBookScan}
                    disabled={!student}
                    className={`w-full border text-stone-200 pl-9 pr-3 py-2 rounded-xl text-xs placeholder-stone-500 focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500/50 transition font-mono ${student ? 'bg-[#131110] border-amber-500/40' : 'bg-[#131110]/50 border-[#2e2720] text-stone-600 cursor-not-allowed'}`}
                  />
                </div>
              </div>
            </div>

            {/* Patron Context File Meta Card */}
            {student ? (
              <div className="bg-[#131110] border border-[#2e2720] rounded-xl p-4 space-y-3 mt-4 lg:mt-0">
                <div className="border-b border-[#2e2720] pb-2.5">
                  <p className="text-[10px] font-bold text-stone-400 uppercase tracking-wide">Verified Profile</p>
                  <p className="font-bold text-stone-100 text-sm mt-0.5">
                    {student.surname}, {student.firstname}
                  </p>
                  <div className="flex items-center gap-2 mt-1 text-xs text-stone-400 font-mono">
                    <span className="bg-[#1a1613] border border-[#2e2720] text-stone-300 px-1.5 py-0.5 rounded-md text-[11px]">ID: {student.cardnumber}</span>
                    <span className="font-sans text-stone-600">•</span>
                    <span>Staged Account Limits: <strong className="text-stone-200">{(student.active_loans || []).length + booksToCheckout.length}/5</strong></span>
                  </div>
                </div>

                {/* Sublayer Active System Holdings Ledger */}
                <div className="space-y-2">
                  <span className="text-[10px] font-bold text-stone-400 uppercase tracking-wider block">Current Outstanding Holdings ({(student.active_loans || []).length})</span>
                  {(student.active_loans || []).length === 0 ? (
                    <p className="text-xs text-stone-500 italic">No historical open book balances pending return.</p>
                  ) : (
                    <div className="max-h-[140px] overflow-y-auto space-y-1.5 pr-1">
                      {(student.active_loans || []).map((loan) => {
                        const style = getLoanStatusStyles(loan.due_date);
                        return (
                          <div key={loan.id} className={`text-xs p-2 border rounded-xl flex items-center justify-between gap-2 ${style.bg}`}>
                            <span className="font-medium truncate flex-1">{loan.book_title}</span>
                            <span className="text-[10px] font-bold whitespace-nowrap flex items-center gap-1 shrink-0 font-mono">
                              <span className={`h-1.5 w-1.5 rounded-full ${style.dot}`} />
                              {style.label} {loan.due_date}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="border border-dashed border-[#2e2720] rounded-xl p-8 text-center text-stone-500 flex flex-col items-center justify-center gap-2 bg-[#131110]/30 lg:h-48">
                <User className="h-5 w-5 text-stone-600" />
                <span className="text-xs text-stone-500">No active patron authenticated profiles staged in workspace memory.</span>
              </div>
            )}
          </div>

          {/* Right Column: Interactive Staging Shelf Table Context (Grid Size 7) */}
          <div className="lg:col-span-7 flex flex-col justify-between min-h-[250px] lg:min-h-0">
            <div className="space-y-3 flex flex-col h-full justify-between">
              <div>
                <div className="flex items-center justify-between border-b border-[#2e2720] pb-2">
                  <h4 className="text-[10px] font-bold text-stone-400 uppercase tracking-wide">Staged Handout Basket ({booksToCheckout.length})</h4>
                  {booksToCheckout.length > 0 && (
                    <button 
                      onClick={() => setBooksToCheckout([])} 
                      className="text-xs text-stone-400 hover:text-rose-400 transition font-medium"
                    >
                      Clear All Staged Items
                    </button>
                  )}
                </div>

                {/* Staging Area Conditional Core View */}
                {booksToCheckout.length > 0 ? (
                  <div className="space-y-2 max-h-[300px] lg:max-h-[360px] overflow-y-auto pt-2 pr-1">
                    {booksToCheckout.map((book) => (
                      <div 
                        key={book.id} 
                        className="group flex items-center justify-between border border-[#2e2720] rounded-xl p-3 bg-[#131110] hover:border-stone-700 transition-all"
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <div className={`p-2 rounded-lg ${book.is_available ? 'bg-emerald-500/10 text-emerald-400' : 'bg-rose-500/10 text-rose-400'}`}>
                            <BookOpen className="h-4 w-4" />
                          </div>
                          <div className="min-w-0">
                            <p className="text-xs font-bold text-stone-100 truncate">{book.title}</p>
                            <p className="text-[11px] text-stone-400 font-mono mt-0.5">
                              Barcode ID: {book.barcode || "N/A"} • 
                              <span className={`ml-1 font-sans font-medium ${book.is_available ? 'text-emerald-400' : 'text-rose-400'}`}>
                                {book.is_available ? " Staged for Handout" : " Unavailable"}
                              </span>
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={() => handleRemoveBook(book.id)}
                          className="text-stone-500 hover:text-rose-400 p-1.5 hover:bg-rose-500/10 rounded-lg transition opacity-100 lg:opacity-0 group-hover:opacity-100"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="border border-dashed border-[#2e2720] rounded-xl p-12 text-center text-stone-500 flex flex-col items-center justify-center gap-3 bg-[#131110]/30 my-2 flex-1">
                    <div className="p-3 bg-[#1a1613] rounded-xl border border-[#2e2720] text-stone-500">
                      <BookOpen className="h-6 w-6" />
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-stone-300">Staging Area Shelf Empty</p>
                      <p className="text-xs text-stone-500 max-w-xs mx-auto mt-0.5">Staged records will appear here before processing output commands.</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Threshold Warnings Box Callout */}
              {student && (student.active_loans || []).length >= 4 && (
                <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-3 flex items-start gap-2.5 text-xs text-amber-300 mt-2">
                  <AlertTriangle className="h-4 w-4 text-amber-400 shrink-0 mt-0.5" />
                  <p className="leading-relaxed">
                    <strong className="text-amber-400">Approaching Upper Threshold Limits:</strong> This patron account holds active files near bounds. Check balances before commitment.
                  </p>
                </div>
              )}
            </div>
          </div>

        </div>

        {/* Modal Command Actions Footer Layout */}
        <div className="px-6 py-4 bg-[#131110] flex items-center justify-end gap-2 border-t border-[#2e2720]">
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="px-4 py-2 border border-[#2e2720] font-mono bg-[#131110] text-stone-300 hover:text-stone-100 hover:bg-[#201c17] font-semibold text-xs rounded-xl transition disabled:opacity-50"
          >
            Close
          </button>
          <button
            type="button"
            disabled={booksToCheckout.length === 0 || isSubmitting}
            onClick={handleCheckout}
            className="inline-flex items-center font-mono gap-1.5 px-4 py-2 bg-amber-500 hover:bg-amber-400 text-stone-950 font-bold text-xs rounded-xl shadow transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <span>{isSubmitting ? "Processing..." : "Checkout"}</span>
            <ArrowRight className="h-4 w-4" />
          </button>
        </div>

      </div>
    </div>
  );
}